import * as React from 'react';
import Image from 'next/image';
import { MapPin, TrendingUp } from 'lucide-react';
import { ResaleMarker } from '@/components/atoms/ResaleMarker';
import { Card, CardContent, CardFooter } from '../../atoms/primitives/card';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Status } from '@/components/atoms/Status';
import { cn } from '../../atoms/primitives/utils';
import { DisplayEventTitle } from '@/lib/format/DisplayEventTitle';
import { toDisplayImageUrl } from '@/lib/image-proxy/toDisplayImageUrl';
import type { WhyLine } from '@/lib/home/personalizationSignals';
import { categoryBadgeClass } from './eventCardCategoryBadge';
import { categoryGradientClass } from './eventCardCategoryGradient';

export type EventCardCategory = 'music' | 'sports' | 'food' | 'tech' | 'art' | 'drinks' | 'default';

export interface EventCardProps {
  title: string;
  /** @deprecated Use `imageUrl` instead. Kept for backward compatibility with DisplayEvent. */
  image?: string;
  /** Primary image URL for the event card. */
  imageUrl?: string;
  category: string;
  date: string;
  venue: string;
  /** Displayed price (e.g. "$25", "Free"). Rendered in the footer next to the date. */
  price?: string;
  /**
   * WEB-1098 — Ticketmaster resale listing (the wire's `resaleListing`). Renders
   * the DS 1.10.0 `.a-resale` marker grouped before the price on the footer row
   * (alone when the price is unknown) and demotes the figure to `--fg-2` / 500,
   * so an above-face number is never met in full-weight ink. Never in the media
   * badge stack; the category badge is untouched.
   */
  resaleListing?: boolean;
  /** Featured variant: teal border + ambient glow shadow (LOCI · CLT). */
  featured?: boolean;
  /** Renders a live distance pill top-left (pairs with `distance`). */
  isLive?: boolean;
  /** Walking/driving distance label (e.g. "0.3 mi"). Used by the live pill and the footer row. */
  distance?: string;
  /** When true, card uses w-full for grid contexts; otherwise default width w-[280px]. */
  fullWidth?: boolean;
  /** When true, applies greyed-out styling and shows a "Past" badge. */
  isPast?: boolean;
  /**
   * Canonical "why" caption derived once on the SSR side via
   * `deriveWhyLine` (WEB-382). `null` / `undefined` omits the caption. It is
   * the card's only taste element: FRE-1418 (taste is reasons, never a score)
   * removed the WEB-386 match ring / percent pill and its `matchScore` prop.
   */
  whyLine?: WhyLine | null;
  /**
   * Optional save affordance (WEB-390). When supplied, rendered as a top-right
   * overlay on the card media — above the category badge so the
   * two corner affordances stack and never overlap. Callers pass the connected
   * `DiscoverSaveButton`; this molecule stays presentational. `undefined`
   * renders nothing (default-off), so the card's other consumers (saved-events
   * grid, map result panel) are unaffected.
   */
  saveSlot?: React.ReactNode;
  className?: string;
  /** Called when the whole card is clicked or keyboard-activated (Enter). */
  onClick?: () => void;
  /** Called when the View Details CTA is clicked. Stops propagation to onClick. */
  onCtaClick?: () => void;
}

export const EventCard = ({
  title,
  image,
  imageUrl,
  category,
  date,
  venue,
  price,
  resaleListing = false,
  featured = false,
  isLive = false,
  distance,
  fullWidth = false,
  isPast = false,
  whyLine,
  saveSlot,
  className,
  onClick,
  onCtaClick,
}: EventCardProps) => {
  // Prefer the legacy `image` prop, then `imageUrl`. Empty / whitespace-only
  // strings count as "no image" so PIP-76's defensive empty-string defaults
  // don't render a broken `<img>` — they fall through to the gradient
  // backdrop. Normalize each candidate independently so a whitespace-only
  // `image` does not suppress a valid `imageUrl`.
  const normalizedImage = image?.trim();
  const normalizedImageUrl = imageUrl?.trim();
  // Route any provider URL through the same-origin proxy (CSP `img-src 'self'`
  // + fetchable by the Next optimizer without a remotePattern). An
  // unproxiable/unknown host collapses to '' → the gradient fallback renders.
  const rawImageSrc = toDisplayImageUrl(normalizedImage || normalizedImageUrl || '');
  const hasImage = rawImageSrc.length > 0;
  const cardAriaLabel = resaleListing
    ? `${category} event: ${title}. Resale listing`
    : `${category} event: ${title}`;
  const interactive = Boolean(onClick);

  const isEmberAccent = whyLine?.accent === 'ember';
  const hasSaveSlot = saveSlot != null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onCtaClick) {
      onCtaClick();
    } else {
      onClick?.();
    }
  };

  return (
    <Card
      role="article"
      aria-label={cardAriaLabel}
      data-featured={featured || undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? handleKeyDown : undefined}
      className={cn(
        'group/card relative min-h-[400px] min-w-touch overflow-hidden flex flex-col gap-0 rounded-ds-lg border border-border',
        'motion-safe:transition-shadow motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.2,0.8,0.2,1)]',
        'shadow-sm hover:shadow-lg',
        // Keyboard focus only — mouse clicks on the CTA should not paint a card-level ring.
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:[&:not(:focus-visible)]:ring-0',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
        interactive && 'cursor-pointer',
        featured && 'border-[1.5px] border-clt-skyline/45 shadow-featured',
        fullWidth ? 'w-full' : 'w-[280px]',
        isPast && 'opacity-60',
        className,
      )}
    >
      <div
        className="relative h-[180px] w-full shrink-0"
        data-testid="event-card-media"
        data-has-image={hasImage ? 'true' : 'false'}
      >
        {hasImage ? (
          <Image
            src={rawImageSrc}
            alt={title}
            fill
            loading="lazy"
            className="object-cover"
            sizes="(max-width: 640px) 100vw, 400px"
          />
        ) : (
          // Category-tinted gradient backdrop — the intentional fallback when
          // the API has no image. We do NOT swap in a generic placeholder
          // bitmap (per WEB-317 acceptance criteria + the LOCI "no fake data"
          // rule). The element is purely decorative; the card's accessible
          // name still comes from the `<article aria-label>` wrapper above.
          <div
            aria-hidden="true"
            data-testid="event-card-gradient-fallback"
            className={cn('absolute inset-0', categoryGradientClass(category))}
          />
        )}

        {/* Category-tinted gradient overlay (top-left → bottom-right) adds depth without hurting contrast. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/0 via-black/0 to-black/20"
        />

        {featured && isLive && distance ? (
          <span
            data-testid="event-card-live-pill"
            className="absolute top-3 left-3 inline-flex items-center gap-1 rounded bg-clt-skyline px-1.5 py-0.5 text-micro font-semibold uppercase tracking-wider text-white"
          >
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full bg-white motion-safe:animate-pulse"
            />
            LIVE · {distance}
          </span>
        ) : null}

        {hasSaveSlot ? (
          <div
            data-testid="event-card-save-slot"
            // Save overlay (WEB-390): top-right of the media, above the match
            // pill / category badge stack so the affordances never overlap.
            // Same hover-opacity ramp as the rail card — 60% at rest, 100% on
            // card hover or save focus, always 100% on touch (no hover cue).
            className={cn(
              'absolute right-2 top-2 z-10',
              'opacity-60 transition-opacity motion-safe:duration-150',
              'group-hover/card:opacity-100 focus-within:opacity-100',
              '[@media(hover:none)]:opacity-100',
            )}
          >
            {saveSlot}
          </div>
        ) : null}

        <div
          className={cn(
            'absolute right-3 flex flex-col items-end gap-1',
            // Drop the category stack below the save overlay when present
            // (the save button is 44×44 at top-2) so they stack vertically.
            hasSaveSlot ? 'top-[3.5rem]' : 'top-3',
          )}
        >
          <Badge
            className={cn(
              'bg-background/95 tracking-wider backdrop-blur-sm',
              categoryBadgeClass(category),
            )}
          >
            {category}
          </Badge>
          {isPast && <Status variant="past">Event has passed</Status>}
        </div>
      </div>

      <CardContent className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 font-display text-[16px] font-bold leading-6">
          {/* WEB-1135: shouting feed titles are title-cased for the eye.
              Alt text and aria-labels below keep the published title. */}
          <DisplayEventTitle title={title} />
        </h3>
        {whyLine ? (
          <p
            data-testid="event-card-why-caption"
            data-accent={isEmberAccent ? 'ember' : 'teal'}
            className={cn(
              'm-0 inline-flex max-w-full items-center gap-1 truncate font-mono text-micro tracking-[0.04em]',
              isEmberAccent ? 'text-clt-ember-light' : 'text-clt-skyline-glow',
            )}
          >
            <TrendingUp className="inline size-[1em] shrink-0" aria-hidden="true" />
            {whyLine.label}
          </p>
        ) : null}
        <div className="mt-auto flex flex-col gap-1 text-[14px] leading-[22px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <MapPin aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {venue}
              {distance ? ` · ${distance}` : ''}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-caption uppercase tracking-wider">{date}</span>
            {resaleListing ? (
              // DS 1.10.0 `reference/resale/cards.html` `.ec__pricegrp`: marker
              // and price form one group on the right, 8px apart.
              <span
                className="inline-flex shrink-0 items-center gap-2"
                data-testid="event-card-price-group"
              >
                <ResaleMarker />
                {price ? (
                  <span className="font-medium text-fg-2" data-testid="event-card-price">
                    {price}
                  </span>
                ) : null}
              </span>
            ) : price ? (
              <span className="font-semibold text-foreground" data-testid="event-card-price">
                {price}
              </span>
            ) : null}
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0">
        <Button
          variant="accent"
          block
          onClick={handleCtaClick}
          aria-label={`View details for ${title}`}
        >
          View Details
        </Button>
      </CardFooter>
    </Card>
  );
};
